import React, { useState } from 'react'

const API_BASE = import.meta.env.VITE_API_URL
  ? `${String(import.meta.env.VITE_API_URL).replace(/\/$/, '')}/api/v1`
  : '/api/v1'

const REGIME_OPTIONS = [
  { value: 'POST_RRA', label: 'Post-RRA 2026 (current)' },
  { value: 'PRE_RRA',  label: 'Pre-RRA (legacy)' },
  { value: 'BOTH',     label: 'Both regimes' },
]

function fmt(n, dp = 1) {
  if (n == null) return '—'
  return Number(n).toFixed(dp)
}

function fmtGbp(n) {
  if (n == null) return '—'
  return `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

function fmtPct(n, dp = 1) {
  if (n == null) return '—'
  return `${Number(n).toFixed(dp)}%`
}

// Inline mini-bar using divs (no recharts dependency needed for this)
function ReturnBar({ p5, p25, p50, p75, p95 }) {
  const min = -15, max = 25
  const pct = v => Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100))
  return (
    <div className="relative h-4 bg-gray-100 rounded overflow-hidden w-full">
      {/* IQR band */}
      <div
        className="absolute h-full bg-emerald-200"
        style={{ left: `${pct(p25)}%`, width: `${pct(p75) - pct(p25)}%` }}
      />
      {/* P5–P95 range */}
      <div
        className="absolute h-2 top-1 bg-emerald-100"
        style={{ left: `${pct(p5)}%`, width: `${pct(p95) - pct(p5)}%` }}
      />
      {/* Median line */}
      <div
        className="absolute top-0 h-full w-0.5 bg-emerald-600"
        style={{ left: `${pct(p50)}%` }}
      />
      {/* Zero line */}
      <div
        className="absolute top-0 h-full w-px bg-gray-400"
        style={{ left: `${pct(0)}%` }}
      />
    </div>
  )
}

// Manual property entry form
function AddPropertyForm({ onAdd }) {
  const [form, setForm] = useState({
    address: '', postcode: '', bedrooms: 3, bathrooms: 1,
    asking_price: '', monthly_rent: '', hmo_flag: false,
    service_tier: 'SL2',
  })
  const [err, setErr] = useState(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = () => {
    if (!form.address || !form.postcode || !form.asking_price || !form.monthly_rent) {
      setErr('Address, postcode, asking price and monthly rent are required.')
      return
    }
    setErr(null)
    onAdd({
      address: { line_1: form.address, city: '', postcode: form.postcode.toUpperCase() },
      financials: {
        asking_price: Number(form.asking_price),
        monthly_rent: Number(form.monthly_rent),
      },
      features: {
        property_type: form.hmo_flag ? 'house' : 'flat',
        tenure: 'freehold',
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
      },
      service_tier: form.service_tier,
      hmo_flag: form.hmo_flag,
    })
    setForm({ address: '', postcode: '', bedrooms: 3, bathrooms: 1, asking_price: '', monthly_rent: '', hmo_flag: false, service_tier: 'SL2' })
  }

  return (
    <div className="space-y-3">
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Address line 1</label>
          <input className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            value={form.address} onChange={e => set('address', e.target.value)} placeholder="e.g. 14 Park Road" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Postcode</label>
          <input className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            value={form.postcode} onChange={e => set('postcode', e.target.value)} placeholder="e.g. N10 3HN" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Service tier</label>
          <select className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
            value={form.service_tier} onChange={e => set('service_tier', e.target.value)}>
            <option value="SL1">SL1 — Let Only</option>
            <option value="SL2">SL2 — Full Management</option>
            <option value="SL3">SL3 — Premium / HMO</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Asking price (£)</label>
          <input className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            type="number" value={form.asking_price} onChange={e => set('asking_price', e.target.value)} placeholder="400000" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Monthly rent (£)</label>
          <input className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            type="number" value={form.monthly_rent} onChange={e => set('monthly_rent', e.target.value)} placeholder="1800" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Bedrooms</label>
          <input className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            type="number" min={1} max={20} value={form.bedrooms} onChange={e => set('bedrooms', e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Bathrooms</label>
          <input className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            type="number" min={1} max={10} value={form.bathrooms} onChange={e => set('bathrooms', e.target.value)} />
        </div>
        <div className="col-span-2 flex items-center gap-2 pt-1">
          <input type="checkbox" id="hmo_flag" checked={form.hmo_flag} onChange={e => set('hmo_flag', e.target.checked)}
            className="rounded border-gray-300 text-emerald-600" />
          <label htmlFor="hmo_flag" className="text-sm text-gray-700">HMO property</label>
        </div>
      </div>
      <button onClick={submit}
        className="w-full bg-emerald-700 hover:bg-emerald-600 text-white text-sm py-2 rounded-lg transition-colors">
        Add to portfolio
      </button>
    </div>
  )
}

export default function Portfolio({ onBack }) {
  const [properties, setProperties] = useState([])
  const [regime, setRegime] = useState('POST_RRA')
  const [iterations, setIterations] = useState(2000)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const addProperty = (prop) => setProperties(ps => [...ps, prop])
  const removeProperty = (i) => setProperties(ps => ps.filter((_, idx) => idx !== i))

  const runSimulation = async () => {
    if (properties.length === 0) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await fetch(`${API_BASE}/portfolio/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ properties, analyses: [], regime, iterations }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.detail || `Simulation failed (${res.status})`)
      }
      setResult(await res.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600 transition-colors">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Portfolio Analysis</h1>
          <p className="text-xs text-gray-500">Book-level Monte Carlo — Austin Chambers & Co</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left panel: inputs */}
        <div className="lg:col-span-1 space-y-4">

          {/* Add property */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Add property</h2>
            <AddPropertyForm onAdd={addProperty} />
          </div>

          {/* Property list */}
          {properties.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">
                Portfolio ({properties.length} {properties.length === 1 ? 'property' : 'properties'})
              </h2>
              <div className="space-y-2">
                {properties.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                    <div>
                      <p className="font-medium text-gray-800 truncate max-w-[160px]">{p.address.line_1}</p>
                      <p className="text-gray-500">{p.address.postcode} · {p.service_tier}{p.hmo_flag ? ' · HMO' : ''}</p>
                    </div>
                    <button onClick={() => removeProperty(i)} className="text-gray-300 hover:text-red-400 transition-colors ml-2">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Run settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-800">Simulation settings</h2>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Regulatory regime</label>
              <select className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                value={regime} onChange={e => setRegime(e.target.value)}>
                {REGIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Iterations: {iterations.toLocaleString()}</label>
              <input type="range" min={500} max={5000} step={500} value={iterations}
                onChange={e => setIterations(Number(e.target.value))}
                className="w-full accent-emerald-600" />
              <div className="flex justify-between text-xs text-gray-400 mt-0.5"><span>500</span><span>5,000</span></div>
            </div>
            <button
              onClick={runSimulation}
              disabled={properties.length === 0 || loading}
              className="w-full bg-gray-900 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Running {iterations.toLocaleString()} paths…
                </>
              ) : 'Run portfolio simulation'}
            </button>
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
        </div>

        {/* Right panel: results */}
        <div className="lg:col-span-2 space-y-4">
          {!result && !loading && (
            <div className="bg-white rounded-xl border border-gray-200 p-10 flex flex-col items-center justify-center text-center">
              <svg className="h-12 w-12 text-gray-200 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-sm font-medium text-gray-400">Add properties and run the simulation</p>
              <p className="text-xs text-gray-400 mt-1">Book-level VaR, return fan, and AC fee drag will appear here</p>
            </div>
          )}

          {result && (
            <>
              {/* Headline metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Median 5y return', value: fmtPct(result.p50_return), sub: 'annualised', color: result.p50_return >= 0 ? 'text-emerald-700' : 'text-red-600' },
                  { label: 'Book VaR (5th pct)', value: fmtPct(result.var_5pct), sub: 'worst 1-in-20 path', color: result.var_5pct >= 0 ? 'text-gray-800' : 'text-red-600' },
                  { label: 'AC fee drag', value: fmtPct(result.total_ac_fee_drag_pct), sub: fmtGbp(result.total_ac_fee_drag_gbp) + '/yr', color: 'text-amber-700' },
                  { label: 'Prob. of loss', value: fmtPct(result.probability_of_loss * 100, 0), sub: 'paths with –ve return', color: result.probability_of_loss < 0.2 ? 'text-gray-800' : 'text-red-600' },
                ].map(m => (
                  <div key={m.label} className="bg-white rounded-xl border border-gray-200 p-4">
                    <p className="text-xs text-gray-500 mb-1">{m.label}</p>
                    <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{m.sub}</p>
                  </div>
                ))}
              </div>

              {/* Return distribution fan */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Book return distribution</h3>
                <p className="text-xs text-gray-500 mb-4">
                  {result.iterations.toLocaleString()} correlated paths · {result.horizon_years}y horizon · {result.regime.replace('_', ' ')}
                </p>
                <div className="space-y-2">
                  {[
                    { label: 'P95 (upside)', v: result.p95_return },
                    { label: 'P75', v: result.p75_return },
                    { label: 'Median', v: result.p50_return, bold: true },
                    { label: 'P25', v: result.p25_return },
                    { label: 'P5 (downside)', v: result.p5_return },
                  ].map(row => (
                    <div key={row.label} className="flex items-center gap-3">
                      <span className={`text-xs w-24 text-right ${row.bold ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
                        {row.label}
                      </span>
                      <div className="flex-1">
                        <ReturnBar p5={result.p5_return} p25={result.p25_return} p50={result.p50_return} p75={result.p75_return} p95={result.p95_return} />
                      </div>
                      <span className={`text-xs w-16 ${row.bold ? 'font-semibold text-gray-800' : 'text-gray-500'} ${row.v < 0 ? 'text-red-600' : ''}`}>
                        {fmtPct(row.v)} p.a.
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-200" /> IQR (P25–P75)</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded bg-emerald-100" /> P5–P95</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-0.5 h-3 bg-emerald-600" /> Median</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-px h-3 bg-gray-400" /> Break-even</span>
                </div>
              </div>

              {/* Per-property fee table */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">Property breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 pr-4 font-medium text-gray-500">Property</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">Gross rent/yr</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">Net rent/yr</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">AC drag</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">Median cashflow</th>
                        <th className="text-right py-2 pl-3 font-medium text-gray-500">P5 / P95</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.properties.map((p, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2.5 pr-4">
                            <p className="font-medium text-gray-800 truncate max-w-[160px]">{p.address.split(',')[0]}</p>
                            <p className="text-gray-400">{p.strategy.replace('_', ' ')} · {p.property_id.slice(0,8)}</p>
                          </td>
                          <td className="text-right py-2.5 px-3 text-gray-700">{fmtGbp(p.gross_annual_rent)}</td>
                          <td className="text-right py-2.5 px-3 text-gray-700">{fmtGbp(p.net_annual_rent)}</td>
                          <td className="text-right py-2.5 px-3 text-amber-700 font-medium">{fmtPct(p.ac_fee_drag_pct)}</td>
                          <td className={`text-right py-2.5 px-3 font-medium ${p.median_annual_cashflow >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            {fmtPct(p.median_annual_cashflow)}
                          </td>
                          <td className="text-right py-2.5 pl-3 text-gray-500">
                            {fmtPct(p.p5_annual_cashflow)} / {fmtPct(p.p95_annual_cashflow)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-gray-200">
                        <td className="py-2.5 pr-4 font-semibold text-gray-800">Total</td>
                        <td className="text-right py-2.5 px-3 font-semibold text-gray-800">{fmtGbp(result.total_gross_rent_annual)}</td>
                        <td className="text-right py-2.5 px-3 font-semibold text-gray-800">{fmtGbp(result.total_net_rent_annual)}</td>
                        <td className="text-right py-2.5 px-3 font-semibold text-amber-700">{fmtPct(result.total_ac_fee_drag_pct)}</td>
                        <td className="py-2.5 px-3" />
                        <td className="py-2.5 pl-3" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  AC fee drag = management fee + annual gas safety (floor). Median/P5/P95 cashflow columns show annualised total return (%) across {result.iterations.toLocaleString()} simulated paths. Estimates only — not financial advice.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
